import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ShieldCheck, AlertCircle, Eye, EyeOff } from "lucide-react";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError("Las contraseñas no coinciden"); return; }
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/accept-invite", { token, name, password });
      if (r.data.access_token) localStorage.setItem("access_token", r.data.access_token);
      setUser(r.data);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Error al aceptar la invitación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center" style={{ background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)" }}>
      <div className="surface p-8" style={{ width: 440, maxWidth: "92vw" }} data-testid="accept-invite-card">
        <div className="flex items-center gap-3 mb-2">
          <div className="kpi-icon-clean" style={{ width: 44, height: 44, color: "#16a34a", background: "#dcfce7", boxShadow: "inset 0 0 0 1px #16a34a33" }}>
            <ShieldCheck size={22}/>
          </div>
          <div>
            <h1 className="font-display text-xl font-extrabold" style={{ letterSpacing: "-0.02em" }}>Acepta tu invitación</h1>
            <p className="text-sm text-gray-500 font-semibold">Crea tu cuenta para acceder al dashboard</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <label className="label-up block mb-1.5">Nombre completo</label>
            <input
              type="text"
              className="text-input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Tu nombre"
              data-testid="invite-name-input"
              autoFocus
            />
          </div>

          <div>
            <label className="label-up block mb-1.5">Contraseña (mín. 8 caracteres)</label>
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                className="text-input w-full pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="invite-password-input"
              />
              <button type="button" onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-900"
                aria-label="Mostrar contraseña">
                {showPwd ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
          </div>

          <div>
            <label className="label-up block mb-1.5">Confirma la contraseña</label>
            <input
              type={showPwd ? "text" : "password"}
              className="text-input w-full"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              placeholder="••••••••"
              data-testid="invite-confirm-input"
            />
          </div>

          {error && (
            <div className="pill bad flex items-center gap-2 w-full" style={{ padding: "8px 12px" }}>
              <AlertCircle size={14}/> {error}
            </div>
          )}

          <button
            type="submit"
            className="btn primary w-full flex items-center justify-center gap-2"
            disabled={loading || !name || !password || !confirm}
            data-testid="invite-submit-btn"
          >
            {loading ? <span className="spinner"/> : null}
            {loading ? "Creando cuenta..." : "Crear cuenta y acceder"}
          </button>
        </form>

        <div className="mt-5 text-xs text-center text-gray-500">
          ¿Ya tienes cuenta? <Link to="/login" className="font-bold text-gray-900 hover:underline">Inicia sesión</Link>
        </div>
      </div>
    </div>
  );
}
